import { useEffect, useState } from "react";
import api from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Plus, KeyRound, LogOut, X, Trash2 } from "lucide-react";
import { DeleteDialog } from "./DeleteDialog";

const ROLES = [
  { value: "platform_admin", label: "Super Admin", desc: "Kelola akun, izin, konfigurasi, audit" },
  { value: "research_admin", label: "Research Admin", desc: "Kelola studi, organisasi, undangan, gelombang" },
  { value: "analyst", label: "Analyst", desc: "Lihat data riset; kontak responden disamarkan" },
];
const MIN_PW = 12;

export default function Team() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", username: "", name: "", role: "analyst", password: "" });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (form.password.length < MIN_PW) return toast.error(`Password minimal ${MIN_PW} karakter`);
    const username = (form.username || "").trim();
    // Guard: browsers may autofill the optional username with the signed-in admin's
    // own login identifier. Never send an email-looking value as a username.
    const safeUsername = username && !username.includes("@") ? username : undefined;
    try {
      await api.post("/admin/users", { ...form, username: safeUsername });
      toast.success(`${form.email} ditambahkan sebagai ${form.role}`);
      setForm({ email: "", username: "", name: "", role: "analyst", password: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menambahkan");
    }
  };

  const setRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.user_id}/role`, { role });
      toast.success("Peran diperbarui");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const toggle = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/toggle-status`);
      toast.success(u.status === "active" ? "Akun dinonaktifkan" : "Akun diaktifkan");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const revoke = async (u) => {
    try {
      const r = await api.post(`/admin/users/${u.user_id}/revoke-sessions`);
      toast.success(`Sesi dicabut (${r.data.revoked})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (resetPw.length < MIN_PW) return toast.error(`Password minimal ${MIN_PW} karakter`);
    try {
      await api.post(`/admin/users/${resetTarget.user_id}/reset-password`, { new_password: resetPw });
      toast.success(`Password ${resetTarget.email} direset — sesi lama dicabut`);
      setResetTarget(null);
      setResetPw("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const changeOwnPassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < MIN_PW) return toast.error(`Password baru minimal ${MIN_PW} karakter`);
    try {
      await api.post("/auth/change-password", pw);
      toast.success("Password Anda diperbarui — sesi lain telah keluar");
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    }
  };

  const inputCls = "rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none";
  const inputStyle = { borderColor: COLORS.line, color: COLORS.ink };

  return (
    <div className="p-6">
      <Eyebrow>Kelola Admin</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Tim &amp; peran</h1>
      <p className="mt-1 text-sm" style={{ color: COLORS.textSoft }}>
        Hanya akun terdaftar yang dapat masuk (email/username + password). Tidak ada pendaftaran mandiri — Super Admin menambahkan akun baru.
      </p>

      <form onSubmit={changeOwnPassword} className="mt-6 rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }}>
        <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Ubah password saya</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <input className={inputCls} style={inputStyle} type="password" placeholder="Password saat ini" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required data-testid="change-current-input" />
          <input className={inputCls} style={inputStyle} type="password" placeholder={`Password baru (min ${MIN_PW})`} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required data-testid="change-new-input" />
          <button className="btn-comiq btn-line justify-center" data-testid="change-password-btn"><KeyRound size={15} /> Ubah password</button>
        </div>
      </form>

      <form onSubmit={add} className="mt-6 rounded-comiq border bg-comiq-surface-alt p-5" style={{ borderColor: COLORS.line }} autoComplete="off">
        <h2 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Tambah admin baru</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <input className={inputCls} style={inputStyle} type="email" placeholder="email@comiq.id *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="off" data-testid="team-email-input" />
          <input className={inputCls} style={inputStyle} placeholder="Username (opsional)" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} name="new-admin-username" autoComplete="off" data-testid="team-username-input" />
          <input className={inputCls} style={inputStyle} placeholder="Nama" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="off" data-testid="team-name-input" />
          <input className={inputCls} style={inputStyle} type="password" placeholder={`Password (min ${MIN_PW}) *`} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete="new-password" data-testid="team-password-input" />
          <select className={inputCls} style={inputStyle} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role-select">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button className="btn-comiq btn-accent justify-center" data-testid="team-add-btn"><Plus size={15} /> Tambahkan</button>
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {users.map((u) => (
          <div key={u.user_id} className="flex flex-wrap items-center gap-3 rounded-comiq border bg-comiq-surface-alt px-5 py-4" style={{ borderColor: COLORS.line }} data-testid={`user-row-${u.email}`}>
            <span className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: COLORS.ink }}>
              {(u.name || u.email).slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold" style={{ color: COLORS.ink }}>{u.name || "—"} {u.user_id === me?.user_id && <span className="text-xs font-normal" style={{ color: COLORS.textSoft }}>(Anda)</span>}</div>
              <div className="text-xs" style={{ color: COLORS.textSoft }}>{u.email}{u.username ? ` · @${u.username}` : ""}</div>
            </div>
            <select
              value={u.role}
              onChange={(e) => setRole(u, e.target.value)}
              className={inputCls}
              style={inputStyle}
              disabled={u.user_id === me?.user_id}
              data-testid={`role-select-${u.email}`}
            >
              {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <span
              className="rounded-full px-2.5 py-1 text-xs font-semibold"
              style={u.status === "active" ? { color: "#15803D", background: "#DCFCE7" } : { color: "#57534E", background: "#EEEAE0" }}
              data-testid={`status-${u.email}`}
            >
              {u.status === "active" ? "Aktif" : "Nonaktif"}
            </span>
            <button onClick={() => setResetTarget(u)} className="btn-comiq btn-line !py-1.5 !text-xs" data-testid={`reset-${u.email}`}>
              <KeyRound size={13} /> Reset password
            </button>
            <button onClick={() => revoke(u)} className="btn-comiq btn-line !py-1.5 !text-xs" data-testid={`revoke-${u.email}`}>
              <LogOut size={13} /> Cabut sesi
            </button>
            {u.user_id !== me?.user_id && (
              <button onClick={() => toggle(u)} className="btn-comiq btn-line !py-1.5 !text-xs" data-testid={`toggle-${u.email}`}>
                {u.status === "active" ? "Nonaktifkan" : "Aktifkan"}
              </button>
            )}
            {u.user_id !== me?.user_id && (
              <button
                onClick={() => setDeleteTarget(u)}
                className="btn-comiq btn-line !py-1.5 !text-xs"
                style={{ color: "#B91C1C", borderColor: "#F1B0AB" }}
                data-testid={`delete-${u.email}`}
              >
                <Trash2 size={13} /> Hapus
              </button>
            )}
          </div>
        ))}
      </div>

      {deleteTarget && (
        <DeleteDialog
          kind="user"
          id={deleteTarget.user_id}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => load()}
        />
      )}

      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5" data-testid="reset-modal">
          <div className="w-full max-w-sm rounded-comiq-lg border bg-comiq-surface-alt p-6" style={{ borderColor: COLORS.line }}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Reset password</h3>
              <button onClick={() => { setResetTarget(null); setResetPw(""); }} data-testid="reset-cancel"><X size={16} style={{ color: COLORS.textSoft }} /></button>
            </div>
            <p className="mt-1 text-xs" style={{ color: COLORS.textSoft }}>{resetTarget.email} — sesi aktif akun ini akan dicabut.</p>
            <form onSubmit={doReset} className="mt-4 space-y-3">
              <input className={`${inputCls} w-full`} style={inputStyle} type="password" placeholder={`Password baru (min ${MIN_PW})`} value={resetPw} onChange={(e) => setResetPw(e.target.value)} required data-testid="reset-password-input" />
              <button className="btn-comiq btn-accent w-full justify-center" data-testid="reset-password-confirm">Simpan password baru</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
